import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Loader2, Printer } from "lucide-react";

/**
 * ClassicChart — Indian OPD-style single-page patient chart.
 *
 * Zero chips, zero emojis, minimal colour. Serif-ish typography, tabular
 * numerals for labs. Designed to look like a paper prescription pad so
 * senior doctors can scan a whole visit in one glance and hand a printed
 * copy to the patient.
 */
export default function ClassicChart() {
  const { viewingPatient, user } = useAuth();
  const patient = viewingPatient || { id: user?.id, name: user?.name };

  const [ct, setCt] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = viewingPatient
        ? `/patient/care-team?patient_id=${viewingPatient.id}`
        : "/patient/care-team";
      const { data } = await api.get(url);
      setCt(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load chart");
    } finally { setLoading(false); }
  }, [viewingPatient]);

  useEffect(() => { load(); }, [load]);

  if (loading || !ct) {
    return (
      <div className="ros-card flex items-center gap-2">
        <Loader2 className="animate-spin" size={16} /> Loading chart…
      </div>
    );
  }

  // Aggregate data from care-team payload
  const encounters = (ct.care_team || []).flatMap((c) => c.encounters || []);
  const latestEnc = encounters[0];
  const diagnoses = ct.diagnoses || [];
  const rxs = (ct.care_team || []).flatMap((c) =>
    (c.prescriptions || []).map((p) => ({ ...p, prescribed_by_name: c.doctor.name }))
  ).filter((p) => (p.status || "active") === "active").slice(0, 12);

  const today = new Date().toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
  const isProvider = user?.role === "provider";

  return (
    <div className="classic-chart" data-testid="classic-chart">
      {/* Print-only stylesheet + toolbar */}
      <style>{`
        .classic-chart { font-family: 'Georgia', 'Times New Roman', serif;
                          color: #111; background: #fff; padding: 24px 32px;
                          max-width: 900px; margin: 0 auto;
                          font-variant-numeric: tabular-nums; }
        .classic-chart h1, .classic-chart h2, .classic-chart .heading
                          { font-family: 'Georgia', serif; }
        .classic-chart .grid-row { display: grid; grid-template-columns: 100px 1fr;
                                    gap: 8px; padding: 3px 0; font-size: 15px; }
        .classic-chart .label   { color: #555; font-weight: 600; text-transform: uppercase;
                                    letter-spacing: 0.05em; font-size: 11px; padding-top: 3px; }
        .classic-chart hr       { border: none; border-top: 1px solid #999; margin: 12px 0; }
        .classic-chart .divider { border-top: 2px double #000; margin: 14px 0 10px; }
        .classic-chart .rx      { font-size: 15px; padding: 4px 0; }
        .classic-chart .rx-num  { display: inline-block; width: 24px; font-weight: bold; }
        .classic-chart table    { width: 100%; border-collapse: collapse; font-size: 14px; }
        .classic-chart td, .classic-chart th { padding: 3px 8px; border: 1px solid #999; text-align: left; }
        .classic-chart th       { background: #f2f2f2; font-weight: 600; }
        .classic-chart .flag-H  { color: #b91c1c; font-weight: 700; }
        .classic-chart .flag-L  { color: #1d4ed8; font-weight: 700; }
        .classic-chart .toolbar { background: #f9f6ef; border-bottom: 1px solid #d4c9a5;
                                    padding: 10px 16px; margin: -24px -32px 20px;
                                    display: flex; justify-content: space-between; align-items: center; }
        @media print {
          body { background: #fff !important; }
          .classic-chart .toolbar, .no-print { display: none !important; }
          .classic-chart { padding: 0; max-width: none; }
        }
      `}</style>

      <div className="toolbar no-print">
        <div style={{ fontSize: 12, color: "#5a4a1f" }}>
          Classic OPD chart · Print or PDF-friendly view
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button data-testid="classic-print"
                  onClick={() => window.print()}
                  style={{ background: "#111", color: "#fff", padding: "6px 14px",
                           borderRadius: 999, border: "none", cursor: "pointer",
                           fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            <Printer size={12} /> Print / PDF
          </button>
        </div>
      </div>

      {/* Header — clinic block */}
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "0.05em" }}>MED NEXUS</div>
        <div style={{ fontSize: 12, color: "#555" }}>
          Multi-Specialty Clinical Intelligence Platform · Consultation Note
        </div>
      </div>
      <div className="divider"></div>

      {/* Patient identification block */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div>
          <div className="grid-row"><span className="label">Name</span><span>{patient.name}</span></div>
          <div className="grid-row"><span className="label">Age/Sex</span><span>{patient.age || "—"} / {patient.sex || "—"}</span></div>
          <div className="grid-row"><span className="label">MRN</span><span>{(patient.id || "").slice(0, 8).toUpperCase()}</span></div>
        </div>
        <div>
          <div className="grid-row"><span className="label">Date</span><span>{today}</span></div>
          <div className="grid-row"><span className="label">Doctor</span><span>{isProvider ? user.name : (latestEnc?.assessment ? "—" : "—")}</span></div>
          <div className="grid-row"><span className="label">Visit #</span><span>{ct.summary?.total_encounters || 1}</span></div>
        </div>
      </div>
      <hr />

      {/* SOAP-style body */}
      <div className="grid-row">
        <span className="label">C/O</span>
        <span>{latestEnc?.chief_complaint || "—"}</span>
      </div>
      <div className="grid-row">
        <span className="label">H/O</span>
        <span>{(latestEnc?.plan) || "Known co-morbidities per prior notes."}</span>
      </div>
      <div className="grid-row">
        <span className="label">Assessment</span>
        <span>{latestEnc?.assessment || "—"}</span>
      </div>

      <div className="divider"></div>

      {/* Diagnosis block */}
      <div className="heading" style={{ fontSize: 14, letterSpacing: "0.05em", marginBottom: 4 }}>
        DIAGNOSIS
      </div>
      {diagnoses.length > 0 ? (
        <ol style={{ marginLeft: 20, fontSize: 15, lineHeight: 1.7 }}
            data-testid="classic-diagnoses">
          {diagnoses.slice(0, 10).map((d, i) => (
            <li key={i}>
              {d.title}
              {d.code && d.code !== "custom" && (
                <span style={{ color: "#555", marginLeft: 8, fontSize: 13 }}>
                  ({d.code})
                </span>
              )}
              {d.added_by_specialty && (
                <span style={{ color: "#777", fontSize: 12, marginLeft: 8 }}>
                  — noted by {d.added_by_name} ({d.added_by_specialty})
                </span>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <div style={{ fontSize: 14, color: "#777", fontStyle: "italic" }}>
          No coded diagnoses recorded.
        </div>
      )}

      <div className="divider"></div>

      {/* Rx block */}
      <div className="heading" style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
        R<span style={{ fontSize: 14, verticalAlign: "super" }}>x</span>
      </div>
      {rxs.length > 0 ? (
        <div data-testid="classic-rx">
          {rxs.map((m, i) => (
            <div key={m.id || i} className="rx">
              <span className="rx-num">{i + 1}.</span>
              <strong>{m.generic_name || m.name}</strong>
              {m.dose && <> — {m.dose}</>}
              {m.frequency && <> — {m.frequency}</>}
              {m.indication && <span style={{ color: "#555" }}> ({m.indication})</span>}
              {m.prescribed_by_name && (
                <span style={{ color: "#777", fontSize: 12, marginLeft: 8 }}>
                  · {m.prescribed_by_name}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: "#777", fontStyle: "italic" }}>
          No active prescriptions.
        </div>
      )}

      <div className="divider"></div>

      {/* Investigations — latest encounter orders */}
      {(latestEnc?.orders || (ct.care_team || []).flatMap((c) => c.orders || []).length > 0) && (
        <>
          <div className="heading" style={{ fontSize: 14, letterSpacing: "0.05em", marginBottom: 4 }}>
            INVESTIGATIONS
          </div>
          <table data-testid="classic-labs">
            <thead>
              <tr><th>Test</th><th>Result</th><th>Unit</th><th>Flag</th></tr>
            </thead>
            <tbody>
              {(ct.care_team || []).flatMap((c) => c.orders || [])
                .flatMap((o) => o.results || [])
                .slice(0, 12)
                .map((r, i) => (
                  <tr key={i}>
                    <td>{r.name || r.code}</td>
                    <td>{r.value}</td>
                    <td>{r.unit}</td>
                    <td className={
                      r.flag === "high" || r.flag === "abnormal" || r.flag === "critical" ? "flag-H"
                      : r.flag === "low" ? "flag-L" : ""
                    }>
                      {r.flag === "normal" ? "" : (r.flag || "").toUpperCase()}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <hr />
        </>
      )}

      {/* Advice + Follow-up */}
      <div className="grid-row">
        <span className="label">Advice</span>
        <span>{latestEnc?.plan || "As per prior plan. Adhere to prescribed regimen."}</span>
      </div>
      <div className="grid-row">
        <span className="label">F/U</span>
        <span>{latestEnc?.next_followup || "As advised · sooner if concerns."}</span>
      </div>

      <div className="divider"></div>

      {/* Signature block */}
      <div style={{ marginTop: 30, display: "flex", justifyContent: "flex-end" }}>
        <div style={{ textAlign: "center", minWidth: 240 }}>
          <div style={{ borderTop: "1px solid #000", paddingTop: 4,
                        fontSize: 14, fontWeight: 700 }}>
            {isProvider ? user.name : "Signature"}
          </div>
          <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
            {isProvider && user.specialty ? `Consultant · ${user.specialty}` : "Attending physician"}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, fontSize: 10, color: "#888", textAlign: "center" }}>
        Generated by Med Nexus on {today} · This is a system-generated note; verify
        details before dispensing.
      </div>
    </div>
  );
}
